import { useState, useEffect, useCallback } from "react";

// @mui material components
import {
  Grid,
  TextField,
  IconButton,
} from "@mui/material";
import Icon from "@mui/material/Icon";

import { customerService } from "../../../../services/customerService";
import { DRAWER_FORM_GRID_SPACING } from "../../../../lib/ui/dialogFormLayout";
import { DrawerForm, DrawerFormAddButton, DrawerFormRow, DrawerFormSection } from "../../../../components/DrawerForm";
import { FormHiddenField, IconAutocomplete } from "../../../../components/forms";
import { CONTACT_TYPE_OPTIONS, ENTITY_STATUS_OPTIONS } from "../../../../lib/ui/iconSelectOptions";
import { useTranslation } from "react-i18next";

interface Contact {
  name: string;
  type: 'PHONE' | 'MOBILE' | 'EMAIL' | 'OTHER' | 'SIEM' | 'BACKUP' | 'FIREWALL' | 'EPP' | 'XDR' | 'DLP' | 'ISP' | 'FILE_AUDIT' | 'MONITORING_TOOL';
  value: string;
}

interface Source {
  name: string;
  type: 'PHONE' | 'MOBILE' | 'EMAIL' | 'OTHER' | 'SIEM' | 'BACKUP' | 'FIREWALL' | 'EPP' | 'XDR' | 'DLP' | 'ISP' | 'FILE_AUDIT' | 'MONITORING_TOOL';
  value: string;
}

const CreateSourceSystemBody = (item: any) => {
  const { t } = useTranslation();
  const dialogItem = item ?? {};
  const { sharedLogoPreview } = dialogItem;
  const [selectedStatus, setSelectedStatus] = useState<string | null>('ACTIVE');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const lockCustomer = !!dialogItem?.lockCustomer;

  const addContact = () => {
    setContacts([...contacts, { name: '', type: 'EMAIL', value: '' }]);
  };

  const removeContact = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const updateContact = (index: number, field: keyof Contact, value: string) => {
    const updatedContacts = contacts.map((contact, i) => 
      i === index ? { ...contact, [field]: value } : contact
    );
    setContacts(updatedContacts);
  };

  const addSource = () => {
    setSources([...sources, { name: '', type: 'EMAIL', value: '' }]);
  };

  const removeSource = (index: number) => {
    setSources(sources.filter((_, i) => i !== index));
  };

  const updateSource = (index: number, field: keyof Source, value: string) => {
    const updatedSources = sources.map((source, i) => 
      i === index ? { ...source, [field]: value } : source
    );
    setSources(updatedSources);
  };

  const searchCustomers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setCustomers([]);
      return;
    }

    try {
      setCustomerLoading(true);
      const response = await customerService.getCustomers(0, 20, query);
      if (response?.content) {
        setCustomers(response.content);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomers([]);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  // Debounced search effect for customers
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!lockCustomer && customerSearchQuery && customerSearchQuery !== selectedCustomer?.name) {
        searchCustomers(customerSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearchQuery, selectedCustomer, searchCustomers, lockCustomer]);

  useEffect(() => {
    if (item?.customerName) {
      setSelectedCustomer({ id: item?.customerId || '', name: item.customerName });
      setCustomerSearchQuery(item.customerName);
      setCustomers([]);
    }
  }, [item?.customerId, item?.customerName]);

  return (
    <DrawerForm>
      <DrawerFormSection title={t("form.identity")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={12}>
            <TextField
              autoFocus
              required
              fullWidth
              id="sourceName"
              name="sourceName"
              label={t("common.name")}
              variant="outlined"
              size="small"
              placeholder={t("form.sourceSystemName")}
            />
          </Grid>
          <Grid size={12}>
            <IconAutocomplete
              options={ENTITY_STATUS_OPTIONS}
              value={selectedStatus}
              onChange={setSelectedStatus}
              label={t("common.status")}
              required
              noOptionsText="No status options found"
            />
            <FormHiddenField name="sourceStatus" value={selectedStatus || ""} />
            <FormHiddenField
              name="sourceLogo"
              value={sharedLogoPreview ? sharedLogoPreview.split(",")[1] || "" : ""}
            />
          </Grid>
        </Grid>
      </DrawerFormSection>

      <DrawerFormSection title={t("form.contacts")} action={<DrawerFormAddButton onClick={addContact} label={t("common.add")} />}>
        {contacts.map((contact, index) => (
          <DrawerFormRow key={index}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.name")}
              value={contact.name}
              onChange={(e) => updateContact(index, "name", e.target.value)}
            />
            <IconAutocomplete
              options={CONTACT_TYPE_OPTIONS}
              value={contact.type}
              onChange={(value) => updateContact(index, "type", value || "EMAIL")}
              noOptionsText="No type options found"
              sx={{ minWidth: 132, maxWidth: 148 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.value")}
              value={contact.value}
              onChange={(e) => updateContact(index, "value", e.target.value)}
            />
            <IconButton size="small" onClick={() => removeContact(index)} color="error">
              <Icon>remove</Icon>
            </IconButton>
          </DrawerFormRow>
        ))}
        <FormHiddenField name="contacts" value={JSON.stringify(contacts)} />
      </DrawerFormSection>

      <DrawerFormSection title={t("form.sources")} action={<DrawerFormAddButton onClick={addSource} label={t("common.add")} />}>
        {sources.map((source, index) => (
          <DrawerFormRow key={index}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.name")}
              value={source.name}
              onChange={(e) => updateSource(index, "name", e.target.value)}
            />
            <IconAutocomplete
              options={CONTACT_TYPE_OPTIONS}
              value={source.type}
              onChange={(value) => updateSource(index, "type", value || "EMAIL")}
              placeholder={t("common.type")}
              noOptionsText="No type options found"
              sx={{ minWidth: 132, maxWidth: 148 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.value")}
              value={source.value}
              onChange={(e) => updateSource(index, "value", e.target.value)}
            />
            <IconButton size="small" onClick={() => removeSource(index)} color="error">
              <Icon>remove</Icon>
            </IconButton>
          </DrawerFormRow>
        ))}
        <FormHiddenField name="sources" value={JSON.stringify(sources)} />
      </DrawerFormSection>
    </DrawerForm>
  );
};

export default CreateSourceSystemBody;